import { GridColDef } from '@mui/x-data-grid-premium';
import { defaultProps } from '../../../utils/helpers/TableHelpers';
import styles from '../../../assets/styles/components/Tables/Tables.module.scss';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { numberShortener } from '../../../utils/numberFormatter';
import { DEFAULT_COLOR_THEME } from '../../../utils';
import { HtmlTooltip } from '../../Tooltip';
import { Fragment } from 'react';

export const demographicsSetColumns = (
  onEdit: (demographicsSetId: string) => void,
  onArchive: (demographicsSetId: string) => void,
  isMobile: boolean,
) => {
  const columns: GridColDef[] = [
    {
      ...defaultProps(isMobile, 300),
      field: 'name',
      headerClassName: styles.header,
      headerName: 'Name',
      sortingOrder: ['desc', 'asc'],
      renderCell: (params: any) => {
        const { name, primary } = params.row;

        return (
          <div className={styles.name}>
            <Tooltip
              title={<Typography variant="body2">{name}</Typography>}
              disableInteractive
            >
              {name}
            </Tooltip>

            {primary ? (
              <Box component="span" ml={1}>
                <Chip
                  label="Default"
                  size="small"
                  sx={{
                    backgroundColor: DEFAULT_COLOR_THEME,
                    color: 'white',
                  }}
                />
              </Box>
            ) : null}
          </div>
        );
      },
    },
    {
      field: 'description',
      width: 250,
      headerClassName: styles.header,
      headerName: 'Description',
      sortingOrder: ['desc', 'asc'],
      renderCell: (params: any) => {
        const { description } = params.row;

        return (
          <Tooltip
            title={<Typography variant="body2">{description}</Typography>}
            disableInteractive
          >
            <div>{description}</div>
          </Tooltip>
        );
      },
    },
    {
      field: 'ageRange',
      width: 120,
      headerClassName: styles.header,
      headerName: 'Age Range',
      sortingOrder: ['desc', 'asc'],
      valueGetter: (_, row) => row.age_min,
      renderCell: (params: any) => {
        const { age_min, age_max } = params.row;

        const ageRange = `${age_min} - ${age_max}`;

        return (
          <Tooltip
            title={<Typography variant="body2">{ageRange}</Typography>}
            disableInteractive
          >
            <div>{ageRange}</div>
          </Tooltip>
        );
      },
    },
    {
      field: 'genders',
      width: 100,
      headerClassName: styles.header,
      headerName: 'Genders',
      sortingOrder: ['desc', 'asc'],
      valueGetter: (_, row) =>
        row.genders.length === 2
          ? 'All'
          : row.genders[0] === 1
          ? 'Male'
          : 'Female',
      renderCell: (params: any) => {
        const { genders } = params.row;

        const gender =
          genders.length === 2 ? 'All' : genders[0] === 1 ? 'Male' : 'Female';

        return (
          <Tooltip
            title={<Typography variant="body2">{gender}</Typography>}
            disableInteractive
          >
            <div>{gender}</div>
          </Tooltip>
        );
      },
    },
    {
      field: 'audienceSize',
      width: 120,
      headerClassName: styles.header,
      headerName: 'Audience Size',
      sortingOrder: ['desc', 'asc'],
      valueGetter: (_, row) => row.minreach,
      renderCell: (params: any) => {
        const { minReach, maxReach } = params.row;

        const audienceSize = `${numberShortener(minReach)} - ${numberShortener(
          maxReach,
        )}`;

        return (
          <Tooltip
            title={
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-line' }}
              >{`${audienceSize}\nBased on your default Geolocation and Audience Set`}</Typography>
            }
            disableInteractive
          >
            <div>{audienceSize}</div>
          </Tooltip>
        );
      },
    },
    {
      field: 'usageCount',
      width: 60,
      headerClassName: styles.header,
      headerName: 'Usage',
      sortingOrder: ['desc', 'asc'],
      valueGetter: (_, row) => row.usageCount,
      renderCell: (params: any) => {
        const { usageCount, adsUsage } = params.row;

        return usageCount > 0 ? (
          <HtmlTooltip
            disableInteractive
            title={
              <Fragment>
                <>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Ads Usages
                  </Typography>

                  {adsUsage?.map((usage: any, index: number) => {
                    return (
                      <Box
                        key={index + 1}
                      >{`• ${usage.name} (${usage.id})`}</Box>
                    );
                  })}
                </>
              </Fragment>
            }
          >
            <div className={styles.name}>{usageCount}</div>
          </HtmlTooltip>
        ) : (
          <div className={styles.name}>{usageCount}</div>
        );
      },
    },
    {
      field: 'action',
      headerClassName: styles.header,
      headerName: 'Action',
      hideSortIcons: true,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      width: 100,
      renderCell: (params) => {
        const { _id } = params.row;

        return (
          <div>
            <IconButton
              onClick={() => onEdit(_id)}
              sx={{ fontSize: '13px', color: '#1976d2' }}
            >
              <ModeEditOutlineOutlinedIcon
                sx={{ fontSize: '18px', color: 'green' }}
              />
            </IconButton>

            <IconButton
              onClick={() => onArchive(_id)}
              sx={{ fontSize: '13px', color: '#1976d2' }}
            >
              <DeleteOutlineOutlinedIcon
                sx={{ fontSize: '18px', color: 'green' }}
              />
            </IconButton>
          </div>
        );
      },
    },
  ];

  return columns;
};
